/**
 * Sprint T-23 · Notification Analytics · Founder/Superadmin dashboard.
 *
 * Consume el endpoint /api/admin/notifications/analytics (agregado read-only).
 * Presenta KPIs, timeline, por canal, por kind, errores, latencias y routing.
 *
 * Filosofía T-23:
 *   · Datos reales · sin invenciones · valores `null` se muestran como "N/A".
 *   · Design System T-20 · sin nuevas dependencias · SVG puro para gráficos.
 *   · WCAG AA · español · desktop-first (uso Founder).
 *   · Cero acoplamiento con el motor T-21 / Notification Center T-22.
 */
import React, { useEffect, useMemo, useState } from "react";
import { RefreshCw, ChartLine, ShieldCheck, AlertTriangle } from "lucide-react";
import { api } from "../../lib/api";
import { KINDS_MAP } from "../../lib/notificationKindsMap";

// ---------------------------------------------------------------------------
// Constantes UI
// ---------------------------------------------------------------------------

const RANGES = [
  { key: "24h", label: "24h" },
  { key: "7d", label: "7 días" },
  { key: "30d", label: "30 días" },
  { key: "90d", label: "90 días" },
];

const CHANNELS = [
  { key: "", label: "Todos los canales" },
  { key: "in_app", label: "In-App" },
  { key: "push", label: "Push" },
  { key: "email", label: "Email" },
];

const CHANNEL_COLORS = {
  in_app: "#f59e0b", // ámbar
  push: "#3b82f6",   // azul
  email: "#10b981",  // verde
};

// ---------------------------------------------------------------------------
// Helpers de formato
// ---------------------------------------------------------------------------

const fmtPct = (v) =>
  v == null ? "N/A" : `${(Number(v) * 100).toFixed(1)}%`;

const fmtInt = (v) => (v == null ? "N/A" : Number(v).toLocaleString("es-ES"));

const fmtMs = (v) => {
  if (v == null) return "N/A";
  if (v < 1000) return `${v} ms`;
  if (v < 60000) return `${(v / 1000).toFixed(1)} s`;
  return `${(v / 60000).toFixed(1)} min`;
};

// ---------------------------------------------------------------------------
// Sub-componente: Filtros
// ---------------------------------------------------------------------------

const AnalyticsFilters = ({
  range, onRangeChange,
  channel, onChannelChange,
  kind, onKindChange,
  loading, onRefresh,
}) => (
  <div
    className="flex flex-wrap items-center gap-3 p-4 bg-[var(--ag-midnight,#0F172A)]/60 border border-[var(--ag-border,#1F2937)] rounded-lg"
    role="toolbar"
    aria-label="Filtros de analytics"
    data-testid="notif-analytics-filters"
  >
    {/* Range chips */}
    <div className="inline-flex rounded-md border border-[var(--ag-border,#1F2937)] overflow-hidden">
      {RANGES.map((r) => {
        const active = range === r.key;
        return (
          <button
            key={r.key}
            type="button"
            onClick={() => onRangeChange(r.key)}
            aria-pressed={active}
            className={[
              "px-3 py-1.5 text-xs font-semibold transition-colors focus:outline-none focus-visible:ring-2 focus-visible:ring-[#FFB703]/50",
              active
                ? "bg-[#FFB703] text-[#0F172A]"
                : "bg-transparent text-gray-300 hover:text-white hover:bg-white/5",
            ].join(" ")}
            data-testid={`notif-analytics-range-${r.key}`}
          >
            {r.label}
          </button>
        );
      })}
    </div>

    {/* Channel */}
    <label className="sr-only" htmlFor="notif-analytics-channel">
      Canal
    </label>
    <select
      id="notif-analytics-channel"
      value={channel}
      onChange={(e) => onChannelChange(e.target.value)}
      className="px-3 py-1.5 text-xs bg-[var(--ag-midnight,#0F172A)]/70 border border-[var(--ag-border,#1F2937)] rounded-md text-white focus:outline-none focus-visible:ring-2 focus-visible:ring-[#FFB703]/50"
      data-testid="notif-analytics-channel-select"
    >
      {CHANNELS.map((c) => (
        <option key={c.key} value={c.key}>
          {c.label}
        </option>
      ))}
    </select>

    {/* Kind */}
    <label className="sr-only" htmlFor="notif-analytics-kind">
      Categoría
    </label>
    <select
      id="notif-analytics-kind"
      value={kind}
      onChange={(e) => onKindChange(e.target.value)}
      className="px-3 py-1.5 text-xs bg-[var(--ag-midnight,#0F172A)]/70 border border-[var(--ag-border,#1F2937)] rounded-md text-white focus:outline-none focus-visible:ring-2 focus-visible:ring-[#FFB703]/50"
      data-testid="notif-analytics-kind-select"
    >
      <option value="">Todas las categorías</option>
      {Object.entries(KINDS_MAP).map(([k, meta]) => (
        <option key={k} value={k}>
          {meta.label}
        </option>
      ))}
    </select>

    <button
      type="button"
      onClick={onRefresh}
      disabled={loading}
      className="ml-auto inline-flex items-center gap-1.5 px-3 py-1.5 text-xs font-semibold text-gray-300 hover:text-white border border-[var(--ag-border,#1F2937)] rounded-md hover:bg-white/5 disabled:opacity-40 focus:outline-none focus-visible:ring-2 focus-visible:ring-[#FFB703]/50"
      data-testid="notif-analytics-refresh"
      aria-label="Refrescar métricas"
    >
      <RefreshCw size={12} className={loading ? "animate-spin" : ""} />
      Refrescar
    </button>
  </div>
);

// ---------------------------------------------------------------------------
// Sub-componente: KPIs top
// ---------------------------------------------------------------------------

const KPICard = ({ label, value, hint, testid, tone = "default" }) => {
  const toneClass =
    tone === "warning" ? "text-amber-400" :
    tone === "danger"  ? "text-rose-400" :
    tone === "success" ? "text-emerald-400" : "text-white";
  return (
    <div
      className="p-4 bg-[var(--ag-midnight,#0F172A)]/60 border border-[var(--ag-border,#1F2937)] rounded-lg"
      data-testid={testid}
    >
      <div className="text-[10px] uppercase tracking-widest text-gray-400 font-semibold">
        {label}
      </div>
      <div className={`text-2xl font-bold mt-1 ${toneClass}`}>{value}</div>
      {hint && <div className="text-[11px] text-gray-500 mt-1">{hint}</div>}
    </div>
  );
};

const AnalyticsKPIs = ({ summary }) => {
  const success = summary?.delivery_success_rate;
  const failure = summary?.delivery_failure_rate;
  const fallback = summary?.fallback_rate;
  const readRate = summary?.inapp_read_rate;
  const dismiss = summary?.inapp_dismiss_rate;
  const p95 = summary?.delivery_latency_p95_ms;
  return (
    <div
      className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-3"
      data-testid="notif-analytics-kpis"
    >
      <KPICard
        label="Total notificaciones"
        value={fmtInt(summary?.total_deliveries)}
        hint={`${fmtInt(summary?.total_inapp)} in-app`}
        testid="notif-analytics-kpi-total"
      />
      <KPICard
        label="Delivery success"
        value={fmtPct(success)}
        tone={success != null && success >= 0.9 ? "success" : success != null && success < 0.5 ? "danger" : "default"}
        testid="notif-analytics-kpi-success"
      />
      <KPICard
        label="Fallos"
        value={fmtPct(failure)}
        tone={failure != null && failure > 0.2 ? "danger" : "default"}
        testid="notif-analytics-kpi-failure"
      />
      <KPICard
        label="Fallback"
        value={fmtPct(fallback)}
        tone={fallback != null && fallback > 0.15 ? "warning" : "default"}
        testid="notif-analytics-kpi-fallback"
      />
      <KPICard
        label="Lectura In-App"
        value={fmtPct(readRate)}
        hint={`Dismiss ${fmtPct(dismiss)}`}
        testid="notif-analytics-kpi-read"
      />
      <KPICard
        label="p95 latencia"
        value={fmtMs(p95)}
        hint="Delivery latency"
        testid="notif-analytics-kpi-latency"
      />
    </div>
  );
};

// ---------------------------------------------------------------------------
// Sub-componente: Timeline SVG
// ---------------------------------------------------------------------------

const AnalyticsTimelineChart = ({ timeline }) => {
  const width = 800;
  const height = 220;
  const padding = { top: 16, right: 16, bottom: 30, left: 40 };
  const innerW = width - padding.left - padding.right;
  const innerH = height - padding.top - padding.bottom;

  if (!timeline || timeline.length === 0) {
    return (
      <div
        className="p-8 border border-[var(--ag-border,#1F2937)] rounded-lg text-center text-sm text-gray-500"
        data-testid="notif-analytics-timeline-empty"
      >
        Sin datos en el periodo seleccionado.
      </div>
    );
  }

  const maxVal = Math.max(1, ...timeline.map((t) => t.total || 0));
  const step = timeline.length > 1 ? innerW / (timeline.length - 1) : innerW;

  const seriesFor = (ch) =>
    timeline
      .map((t, i) => {
        const x = padding.left + i * step;
        const y = padding.top + innerH - ((t[ch] || 0) / maxVal) * innerH;
        return `${i === 0 ? "M" : "L"}${x.toFixed(1)},${y.toFixed(1)}`;
      })
      .join(" ");

  const yTicks = [0, 0.25, 0.5, 0.75, 1].map((f) => Math.round(maxVal * f));

  return (
    <div
      className="p-4 bg-[var(--ag-midnight,#0F172A)]/60 border border-[var(--ag-border,#1F2937)] rounded-lg"
      data-testid="notif-analytics-timeline"
    >
      <div className="flex items-center justify-between mb-3">
        <div className="text-sm font-semibold text-white">Evolución temporal</div>
        <div className="flex items-center gap-3 text-[11px] text-gray-400">
          {Object.entries(CHANNEL_COLORS).map(([ch, color]) => (
            <span key={ch} className="inline-flex items-center gap-1">
              <span
                className="inline-block w-2.5 h-2.5 rounded-full"
                style={{ background: color }}
                aria-hidden="true"
              />
              {ch}
            </span>
          ))}
        </div>
      </div>
      <svg
        viewBox={`0 0 ${width} ${height}`}
        className="w-full h-auto"
        role="img"
        aria-label="Evolución temporal de notificaciones por canal"
      >
        {/* Y-axis grid */}
        {yTicks.map((tick, i) => {
          const y = padding.top + innerH - (i / (yTicks.length - 1)) * innerH;
          return (
            <g key={i}>
              <line
                x1={padding.left}
                x2={width - padding.right}
                y1={y}
                y2={y}
                stroke="#1F2937"
                strokeWidth="1"
              />
              <text
                x={padding.left - 6}
                y={y + 3}
                textAnchor="end"
                fill="#6B7280"
                fontSize="9"
              >
                {tick}
              </text>
            </g>
          );
        })}

        {/* Series */}
        {Object.entries(CHANNEL_COLORS).map(([ch, color]) => (
          <path
            key={ch}
            d={seriesFor(ch)}
            fill="none"
            stroke={color}
            strokeWidth="2"
            strokeLinejoin="round"
            data-testid={`notif-analytics-timeline-series-${ch}`}
          />
        ))}

        {/* X-axis labels (first/mid/last · dedupe cuando length ≤ 2) */}
        {Array.from(
          new Set(
            [0, Math.floor(timeline.length / 2), timeline.length - 1].filter(
              (i) => i >= 0 && i < timeline.length,
            ),
          ),
        ).map((i) => {
          const x = padding.left + i * step;
          const label = String(timeline[i].bucket || "").slice(5, 16);
          return (
            <text
              key={`xlabel-${i}`}
              x={x}
              y={height - 8}
              textAnchor="middle"
              fill="#6B7280"
              fontSize="9"
            >
              {label}
            </text>
          );
        })}
      </svg>
    </div>
  );
};

// ---------------------------------------------------------------------------
// Sub-componente: By-channel breakdown (barras horizontales)
// ---------------------------------------------------------------------------

const AnalyticsByChannelChart = ({ byChannel }) => {
  const maxTotal = Math.max(1, ...(byChannel || []).map((r) => r.total || 0));
  return (
    <div
      className="p-4 bg-[var(--ag-midnight,#0F172A)]/60 border border-[var(--ag-border,#1F2937)] rounded-lg"
      data-testid="notif-analytics-by-channel"
    >
      <div className="text-sm font-semibold text-white mb-3">
        Delivery por canal
      </div>
      <div className="space-y-3">
        {(byChannel || []).map((r) => {
          const total = r.total || 0;
          const pct = (n) => (total > 0 ? (n / total) * 100 : 0);
          const color = CHANNEL_COLORS[r.channel] || "#6B7280";
          return (
            <div key={r.channel} data-testid={`notif-analytics-channel-row-${r.channel}`}>
              <div className="flex items-center justify-between text-xs mb-1">
                <span className="flex items-center gap-1.5 text-white font-medium">
                  <span
                    className="inline-block w-2.5 h-2.5 rounded-full"
                    style={{ background: color }}
                    aria-hidden="true"
                  />
                  <span className="uppercase tracking-wider text-[10px]">{r.channel}</span>
                </span>
                <span className="text-gray-400">
                  {fmtInt(total)} · {fmtPct(r.success_rate)} éxito
                </span>
              </div>
              <div
                className="h-3 w-full bg-[var(--ag-midnight,#0F172A)] rounded-full overflow-hidden flex"
                style={{
                  boxShadow: "inset 0 0 0 1px #1F2937",
                }}
              >
                <div
                  className="h-full bg-emerald-500/80"
                  style={{ width: `${pct(r.sent)}%` }}
                  title={`Enviados: ${r.sent}`}
                />
                <div
                  className="h-full bg-rose-500/80"
                  style={{ width: `${pct(r.failed)}%` }}
                  title={`Fallidos: ${r.failed}`}
                />
                <div
                  className="h-full bg-amber-500/70"
                  style={{ width: `${pct(r.skipped)}%` }}
                  title={`Skipped: ${r.skipped}`}
                />
                <div
                  className="h-full bg-blue-500/70"
                  style={{ width: `${pct(r.superseded)}%` }}
                  title={`Superseded: ${r.superseded}`}
                />
              </div>
            </div>
          );
        })}
      </div>
      <div className="flex flex-wrap items-center gap-3 mt-4 text-[10px] text-gray-500">
        <span className="flex items-center gap-1"><span className="w-2 h-2 bg-emerald-500/80 rounded-sm" />Sent</span>
        <span className="flex items-center gap-1"><span className="w-2 h-2 bg-rose-500/80 rounded-sm" />Failed</span>
        <span className="flex items-center gap-1"><span className="w-2 h-2 bg-amber-500/70 rounded-sm" />Skipped</span>
        <span className="flex items-center gap-1"><span className="w-2 h-2 bg-blue-500/70 rounded-sm" />Superseded</span>
      </div>
    </div>
  );
};

// ---------------------------------------------------------------------------
// Sub-componente: Tabla por kind
// ---------------------------------------------------------------------------

const AnalyticsByKindTable = ({ byKind }) => {
  if (!byKind || byKind.length === 0) {
    return (
      <div
        className="p-6 border border-[var(--ag-border,#1F2937)] rounded-lg text-center text-xs text-gray-500"
        data-testid="notif-analytics-by-kind-empty"
      >
        Sin actividad por categoría en el periodo.
      </div>
    );
  }
  return (
    <div
      className="overflow-hidden border border-[var(--ag-border,#1F2937)] rounded-lg"
      data-testid="notif-analytics-by-kind"
    >
      <div className="px-4 py-2.5 bg-[var(--ag-midnight,#0F172A)]/70 border-b border-[var(--ag-border,#1F2937)]">
        <div className="text-sm font-semibold text-white">Actividad por categoría</div>
      </div>
      <table className="w-full text-xs">
        <thead className="bg-[var(--ag-midnight,#0F172A)]/50 text-[10px] uppercase tracking-widest text-gray-400">
          <tr>
            <th className="text-left px-4 py-2 font-semibold">Categoría</th>
            <th className="text-right px-3 py-2 font-semibold">Total</th>
            <th className="text-right px-3 py-2 font-semibold">Sent</th>
            <th className="text-right px-3 py-2 font-semibold">Failed</th>
            <th className="text-right px-3 py-2 font-semibold">Read %</th>
            <th className="text-right px-3 py-2 font-semibold">Dismiss %</th>
            <th className="text-right px-3 py-2 font-semibold">Fallback</th>
          </tr>
        </thead>
        <tbody className="divide-y divide-[var(--ag-border,#1F2937)]/60">
          {byKind.map((row) => {
            const meta = KINDS_MAP[row.kind];
            return (
              <tr
                key={row.kind}
                className="hover:bg-white/[0.02]"
                data-testid={`notif-analytics-kind-row-${row.kind}`}
              >
                <td className="px-4 py-2 text-white">
                  {meta?.label || row.kind}
                </td>
                <td className="px-3 py-2 text-right text-gray-200 tabular-nums">
                  {fmtInt(row.total_deliveries)}
                </td>
                <td className="px-3 py-2 text-right text-emerald-300 tabular-nums">
                  {fmtInt(row.sent)}
                </td>
                <td className="px-3 py-2 text-right text-rose-300 tabular-nums">
                  {fmtInt(row.failed)}
                </td>
                <td className="px-3 py-2 text-right text-gray-200 tabular-nums">
                  {fmtPct(row.inapp_read_rate)}
                </td>
                <td className="px-3 py-2 text-right text-gray-200 tabular-nums">
                  {fmtPct(row.inapp_dismiss_rate)}
                </td>
                <td className="px-3 py-2 text-right text-amber-300 tabular-nums">
                  {fmtInt(row.fallback_count)}
                </td>
              </tr>
            );
          })}
        </tbody>
      </table>
    </div>
  );
};

// ---------------------------------------------------------------------------
// Sub-componente: Errores
// ---------------------------------------------------------------------------

const AnalyticsErrorsTable = ({ errors }) => {
  if (!errors || errors.length === 0) {
    return (
      <div
        className="p-6 border border-[var(--ag-border,#1F2937)] rounded-lg text-center text-xs text-gray-500"
        data-testid="notif-analytics-errors-empty"
      >
        <ShieldCheck size={16} className="inline text-emerald-400 mb-1" />
        <div className="mt-1">Sin errores registrados en el periodo.</div>
      </div>
    );
  }
  return (
    <div
      className="overflow-hidden border border-[var(--ag-border,#1F2937)] rounded-lg"
      data-testid="notif-analytics-errors"
    >
      <div className="px-4 py-2.5 bg-[var(--ag-midnight,#0F172A)]/70 border-b border-[var(--ag-border,#1F2937)]">
        <div className="text-sm font-semibold text-white flex items-center gap-1.5">
          <AlertTriangle size={13} className="text-rose-300" /> Errores por canal
        </div>
      </div>
      <table className="w-full text-xs">
        <thead className="bg-[var(--ag-midnight,#0F172A)]/50 text-[10px] uppercase tracking-widest text-gray-400">
          <tr>
            <th className="text-left px-4 py-2 font-semibold">Canal</th>
            <th className="text-left px-3 py-2 font-semibold">Estado</th>
            <th className="text-left px-3 py-2 font-semibold">Tipo</th>
            <th className="text-right px-3 py-2 font-semibold">Count</th>
            <th className="text-right px-3 py-2 font-semibold">Rate</th>
          </tr>
        </thead>
        <tbody className="divide-y divide-[var(--ag-border,#1F2937)]/60">
          {errors.map((e, idx) => (
            <tr
              key={`${e.channel}-${e.status}-${e.error_type}-${idx}`}
              className="hover:bg-white/[0.02]"
              data-testid={`notif-analytics-error-row-${idx}`}
            >
              <td className="px-4 py-2 text-white uppercase tracking-wider text-[10px]">
                {e.channel}
              </td>
              <td className="px-3 py-2 text-gray-300">{e.status}</td>
              <td className="px-3 py-2 text-gray-300">{e.error_type}</td>
              <td className="px-3 py-2 text-right text-rose-300 tabular-nums">
                {fmtInt(e.count)}
              </td>
              <td className="px-3 py-2 text-right text-gray-200 tabular-nums">
                {fmtPct(e.rate)}
              </td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
};

// ---------------------------------------------------------------------------
// Sub-componente: Latencia
// ---------------------------------------------------------------------------

const AnalyticsLatencyTable = ({ latency }) => {
  const rows = [
    { key: "delivery", label: "Delivery (provider)", data: latency?.delivery, unit: "ms" },
    { key: "inapp_read", label: "Lectura In-App", data: latency?.inapp_read, unit: "ms" },
    { key: "click", label: "Click", data: latency?.click, unit: "ms" },
  ];
  return (
    <div
      className="overflow-hidden border border-[var(--ag-border,#1F2937)] rounded-lg"
      data-testid="notif-analytics-latency"
    >
      <div className="px-4 py-2.5 bg-[var(--ag-midnight,#0F172A)]/70 border-b border-[var(--ag-border,#1F2937)]">
        <div className="text-sm font-semibold text-white">Latencias (p50 · p95 · p99)</div>
      </div>
      <table className="w-full text-xs">
        <thead className="bg-[var(--ag-midnight,#0F172A)]/50 text-[10px] uppercase tracking-widest text-gray-400">
          <tr>
            <th className="text-left px-4 py-2 font-semibold">Métrica</th>
            <th className="text-right px-3 py-2 font-semibold">p50</th>
            <th className="text-right px-3 py-2 font-semibold">p95</th>
            <th className="text-right px-3 py-2 font-semibold">p99</th>
            <th className="text-right px-3 py-2 font-semibold">n</th>
          </tr>
        </thead>
        <tbody className="divide-y divide-[var(--ag-border,#1F2937)]/60">
          {rows.map((r) => (
            <tr
              key={r.key}
              data-testid={`notif-analytics-latency-row-${r.key}`}
            >
              <td className="px-4 py-2 text-white">{r.label}</td>
              <td className="px-3 py-2 text-right text-gray-200 tabular-nums">
                {fmtMs(r.data?.p50)}
              </td>
              <td className="px-3 py-2 text-right text-gray-200 tabular-nums">
                {fmtMs(r.data?.p95)}
              </td>
              <td className="px-3 py-2 text-right text-gray-200 tabular-nums">
                {fmtMs(r.data?.p99)}
              </td>
              <td className="px-3 py-2 text-right text-gray-400 tabular-nums">
                {r.data?.sample_size == null ? "N/A" : fmtInt(r.data.sample_size)}
              </td>
            </tr>
          ))}
        </tbody>
      </table>
      <div className="px-4 py-2 text-[10px] text-gray-500 bg-[var(--ag-midnight,#0F172A)]/30 border-t border-[var(--ag-border,#1F2937)]">
        Click latency no es medible con la instrumentación actual (sin tracking pixel/link).
      </div>
    </div>
  );
};

// ---------------------------------------------------------------------------
// Sub-componente: Routing
// ---------------------------------------------------------------------------

const AnalyticsRoutingCard = ({ routing, engagement }) => (
  <div
    className="grid grid-cols-2 md:grid-cols-4 gap-3"
    data-testid="notif-analytics-routing"
  >
    <KPICard
      label="Broadcast"
      value={fmtInt(routing?.broadcast_count)}
      hint="Envío paralelo multi-canal"
      testid="notif-analytics-routing-broadcast"
    />
    <KPICard
      label="Cascade"
      value={fmtInt(routing?.cascade_count)}
      hint="Primary + fallback chain"
      testid="notif-analytics-routing-cascade"
    />
    <KPICard
      label="Primary"
      value={fmtInt(routing?.primary_count)}
      hint="Canales de primera línea"
      testid="notif-analytics-routing-primary"
    />
    <KPICard
      label="Fallback executed"
      value={fmtInt(routing?.fallback_count)}
      hint={`Éxito: ${fmtInt(routing?.fallback_success_count)}`}
      testid="notif-analytics-routing-fallback"
    />
    <KPICard
      label="In-App total"
      value={fmtInt(engagement?.total)}
      hint={`Unread ${fmtInt(engagement?.unread)}`}
      testid="notif-analytics-engagement-total"
    />
    <KPICard
      label="In-App leídas"
      value={fmtInt(engagement?.read)}
      hint={fmtPct(engagement?.read_rate)}
      testid="notif-analytics-engagement-read"
    />
    <KPICard
      label="In-App descartadas"
      value={fmtInt(engagement?.dismissed)}
      hint={fmtPct(engagement?.dismiss_rate)}
      testid="notif-analytics-engagement-dismissed"
    />
    <KPICard
      label="Kinds oficiales"
      value="7"
      hint="in_app · push · email"
      testid="notif-analytics-kinds-info"
    />
  </div>
);

// ---------------------------------------------------------------------------
// Componente principal
// ---------------------------------------------------------------------------

export default function NotificationAnalyticsTab() {
  const [range, setRange] = useState("24h");
  const [channel, setChannel] = useState("");
  const [kind, setKind] = useState("");
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const fetchAnalytics = React.useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const params = new URLSearchParams({ range });
      if (channel) params.set("channel", channel);
      if (kind) params.set("kind", kind);
      const r = await api.get(
        `/admin/notifications/analytics?${params.toString()}`,
      );
      setData(r?.data || null);
    } catch (e) {
      const status = e?.response?.status;
      if (status === 401 || status === 403) {
        setError("Acceso denegado · esta sección es exclusiva de Founder/Superadmin.");
      } else {
        setError(e?.response?.data?.detail || "Error al cargar analytics");
      }
    } finally {
      setLoading(false);
    }
  }, [range, channel, kind]);

  useEffect(() => {
    fetchAnalytics();
  }, [fetchAnalytics]);

  const notes = useMemo(() => data?.notes || [], [data]);

  return (
    <div className="space-y-5" data-testid="notif-analytics-tab">
      {/* Header */}
      <div className="flex items-center gap-2">
        <ChartLine size={18} className="text-[#FFB703]" />
        <div>
          <h2 className="text-lg font-semibold text-white leading-tight">
            Engagement · Delivery · Routing · Latencia
          </h2>
          <p className="text-xs text-gray-400 mt-0.5">
            Sprint T-23 · métricas basadas exclusivamente en datos reales del motor T-21.
          </p>
        </div>
      </div>

      <AnalyticsFilters
        range={range}
        onRangeChange={setRange}
        channel={channel}
        onChannelChange={setChannel}
        kind={kind}
        onKindChange={setKind}
        loading={loading}
        onRefresh={fetchAnalytics}
      />

      {error && (
        <div
          className="p-4 border border-rose-500/40 bg-rose-500/10 rounded-lg text-sm text-rose-200"
          data-testid="notif-analytics-error"
        >
          {error}
        </div>
      )}

      {loading && !data && (
        <div
          className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-3"
          data-testid="notif-analytics-loading"
          aria-busy="true"
        >
          {Array.from({ length: 6 }).map((_, i) => (
            <div
              key={i}
              className="p-4 bg-[var(--ag-midnight,#0F172A)]/40 border border-[var(--ag-border,#1F2937)]/60 rounded-lg animate-pulse h-[86px]"
            />
          ))}
        </div>
      )}

      {data && (
        <>
          <AnalyticsKPIs summary={data.summary} />
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
            <AnalyticsTimelineChart timeline={data.timeline} />
            <AnalyticsByChannelChart byChannel={data.by_channel} />
          </div>
          <AnalyticsByKindTable byKind={data.by_kind} />
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
            <AnalyticsErrorsTable errors={data.errors} />
            <AnalyticsLatencyTable latency={data.latency} />
          </div>
          <AnalyticsRoutingCard routing={data.routing} engagement={data.engagement_inapp} />

          {notes.length > 0 && (
            <div
              className="p-3 bg-[var(--ag-midnight,#0F172A)]/40 border border-[var(--ag-border,#1F2937)] rounded-lg space-y-1"
              data-testid="notif-analytics-notes"
            >
              <div className="text-[10px] uppercase tracking-widest text-gray-500 font-semibold">
                Notas técnicas
              </div>
              <ul className="text-[11px] text-gray-400 list-disc list-inside space-y-0.5">
                {notes.map((n, i) => (
                  <li key={i}>{n}</li>
                ))}
              </ul>
            </div>
          )}
        </>
      )}
    </div>
  );
}
